import Spinner from 'Components/Spinner/Spinner';
import { DateTime } from 'luxon';
import { FunctionComponent, useMemo } from 'react';
import DataTable, { TableColumn, createTheme } from 'react-data-table-component';
import { RxReset } from 'react-icons/rx';
import { Currency, toCurrency } from 'utils/currency';

import { MessageWrapper, PayoutsDataTableContainer, ReverseButton } from '../styles';
import EmptyComponent from './EmptyComponent';
import ErrorComponent from './ErrorComponent';
import { TableStyleSettings, TableThemeSettings } from './constants';
import { PayoutsQueryEdges } from './types';

// createTheme creates a new theme named solarized that overrides the build in dark theme
createTheme('polarized', TableThemeSettings);

interface Props {
  payoutList?: Array<PayoutsQueryEdges[number]>;
  fetchListLoader: boolean;
  totalRows: number;
  handlePerRowsChange: (newPerPage: number, page: number) => void;
  handlePageChange: (page: number) => void;
  limit: number;
  activePage: number;
  isErrorRequest?: boolean;
  reversePayout: (merchantId: string, payoutId: string) => void;
  hideMerchantColumn?: boolean;
}

function PayoutDataTable({
  payoutList,
  totalRows,
  limit,
  fetchListLoader,
  handlePageChange,
  handlePerRowsChange,
  activePage,
  isErrorRequest,
  reversePayout,
  hideMerchantColumn = false,
}: Props) {
  const ReverseIcon = RxReset as FunctionComponent;

  const columns: TableColumn<PayoutsQueryEdges[number]>[] = useMemo(() => {
    const first = [
      {
        name: 'Amount',
        selector: (row: PayoutsQueryEdges[number]) =>
          toCurrency(row.node.amount.minorCurrencyUnits, row.node.amount.currency as Currency),
        width: '100px',
        right: true,
      },
      {
        name: '',
        selector: (row: PayoutsQueryEdges[number]) => row.node.amount.currency,
        width: '70px',
      },
      {
        name: 'Payout ID',
        selector: (row: PayoutsQueryEdges[number]) => row.node.payoutId,
        minWidth: '150px',
      },
    ];

    const second = [
      {
        name: 'Merchant brand',
        selector: (row: PayoutsQueryEdges[number]) => {
          return <div>{`${row.node.merchant?.businessProfile.brandName}`}</div>;
        },
        width: '220px',
      },
      {
        name: 'Business name',
        selector: (row: PayoutsQueryEdges[number]) => {
          return <div>{`${row.node.merchant?.businessProfile.businessName}`}</div>;
        },
        width: '220px',
      },
    ];

    const third = [
      {
        name: 'Created on',
        selector: (row: PayoutsQueryEdges[number]) =>
          row.node.createdAt ? `${DateTime.fromISO(row.node.createdAt).toFormat('dd/LL/yyyy, HH:mm')}` : '',
        minWidth: '120px',
      },
      {
        name: 'Available on',
        selector: (row: PayoutsQueryEdges[number]) =>
          row.node.settlementDate ? `${DateTime.fromISO(row.node.settlementDate).toFormat('dd/LL/yyyy, HH:mm')}` : '',
        minWidth: '120px',
      },
      {
        name: 'Status',
        cell: (row: PayoutsQueryEdges[number]) => {
          return <div>{`${row.node.status}`}</div>;
        },
        width: '75px',
      },
      {
        name: '',
        cell: (row: PayoutsQueryEdges[number]) => {
          return row.node.status === 'Failed' ? (
            <div className="hover-button-wrapper">
              <ReverseButton
                className="hover-button"
                title="Reverse Payout"
                onClick={() =>
                  row.node.merchant ? reversePayout(row.node.merchant?.merchantId, row.node.payoutId) : null
                }
              >
                <ReverseIcon />
              </ReverseButton>
            </div>
          ) : (
            ''
          );
        },
        width: '50px',
      },
    ];

    return hideMerchantColumn ? [...first, ...third] : [...first, ...second, ...third];
  }, [ReverseIcon, hideMerchantColumn, reversePayout]);

  return (
    <PayoutsDataTableContainer>
      <DataTable
        pagination
        responsive
        columns={columns}
        data={payoutList ?? []}
        customStyles={TableStyleSettings}
        theme="polarized"
        className=""
        progressPending={fetchListLoader}
        progressComponent={
          <MessageWrapper>
            <Spinner color="#0016D1" height="40px" width="40px" />
          </MessageWrapper>
        }
        noDataComponent={
          isErrorRequest ? (
            <MessageWrapper>
              <ErrorComponent />
            </MessageWrapper>
          ) : (
            <MessageWrapper>
              <EmptyComponent />
            </MessageWrapper>
          )
        }
        paginationServer
        paginationTotalRows={totalRows}
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
        paginationPerPage={limit}
        paginationDefaultPage={activePage}
        sortServer
        persistTableHead
        defaultSortFieldId={4}
        defaultSortAsc={false}
        paginationRowsPerPageOptions={[20, 40, 60, 80, 100]}
      />
    </PayoutsDataTableContainer>
  );
}

export default PayoutDataTable;
